/**
 * Site detail screen.
 *
 * Mounted at /evaluation-sites/:siteId rather than under an evaluation, because
 * a site is owner-scoped and may belong to zero or many evaluations. One route
 * therefore serves both the evaluation Sites tab and the operator-wide
 * Evaluation Sites page.
 */

import { useEffect, useState } from 'react'
import { Outlet, useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import SecondaryNavCN from 'components/SecondaryNavCN/SecondaryNavCN'
import { Building2, ImageIcon, StickyNote } from 'lucide-react'
import routes from 'constants/routes'
import { fetchSiteById } from 'slices/evaluationSiteSlice'
import SiteEditSheet from 'screens/evaluations/components/SiteEditSheet'

/** Read by the tab components via useOutletContext(). */
export interface SiteOutletContext {
	site: any
	siteId: string
}

const Sites_ShowCN = () => {
	const { siteId } = useParams()
	const dispatch = useDispatch()
	const navigate = useNavigate()

	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [editOpen, setEditOpen] = useState(false)

	const site = useSelector((state: any) => (siteId ? state.evaluationSites?.entities?.[siteId] : null))

	// Always refetch: the site may have been reached by a direct URL, and the
	// list views populate only the fields they need.
	useEffect(() => {
		if (siteId) dispatch(fetchSiteById(siteId) as any)
	}, [dispatch, siteId])

	if (!site || !siteId) {
		return (
			<div className="flex h-screen overflow-hidden bg-gray-50">
				<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />
				<div className="flex flex-1 flex-col overflow-hidden">
					<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />
					<main className="flex-1 overflow-y-auto p-6">
						<div className="py-20 text-center text-sm text-gray-400">Loading site...</div>
					</main>
				</div>
			</div>
		)
	}

	const outletContext: SiteOutletContext = { site, siteId }

	const navLinks = [
		{
			linkText: 'Overview',
			linkTo: `${routes.ALL_SITES}/${siteId}`,
			icon: Building2,
			end: true,
		},
		{ linkText: 'Images', linkTo: `${routes.ALL_SITES}/${siteId}/images`, icon: ImageIcon },
		{ linkText: 'Notes', linkTo: `${routes.ALL_SITES}/${siteId}/notes`, icon: StickyNote },
	]

	const locationLine = [site.address, site.city, site.state, site.postalCode]
		.filter(Boolean)
		.join(', ')

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex flex-1 flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				{/* Detail header — standard MDT _Show pattern */}
				<div className="flex flex-shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-6 py-2">
					<button
						type="button"
						onClick={() => setEditOpen(true)}
						className="m-0 cursor-pointer border-none bg-transparent p-0 text-sm font-semibold text-[#0080fe] hover:underline"
					>
						{site.name}
					</button>
					{locationLine && <span className="text-sm text-gray-500">{locationLine}</span>}
					{site.propertyType && (
						<span className="text-sm text-gray-500">Type: {site.propertyType}</span>
					)}
				</div>

				<SecondaryNavCN
					links={navLinks}
					backLink={{ link: routes.ALL_SITES, title: 'Evaluation Sites' }}
					sourceName="Evaluation Sites"
				/>

				<div className="flex flex-1 overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						<Outlet context={outletContext} />
					</main>
				</div>
			</div>

			<SiteEditSheet
				open={editOpen}
				site={site}
				onClose={() => setEditOpen(false)}
				// No evalId: this route is owner-scoped, so the sheet offers a
				// permanent delete rather than an unlink from one evaluation.
				// The entity is then gone from the store, so leave rather than
				// sit on the loading state; `replace` keeps the deleted site
				// out of the back history.
				onDeleted={() => navigate(routes.ALL_SITES, { replace: true })}
			/>
		</div>
	)
}

export default Sites_ShowCN
